import { isInPast, nextOccurrence, parse, RepeatParsingResult, TimeOfDay, Weekday } from '@focuslee/shared';
import dayjs from 'dayjs';
import { Repeat, RepeatingType, TimeOfDay as GraphQLTimeOfDay } from '../client/generated/graphql';
import { ListQuest } from '../client/types';
import { i18next } from '../i18n';
import { dayjsStartOfDay } from './datetime';

export function capitalizeFirstLetter(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1);
}

export const findQuestByName = (quests: ListQuest[], name: string): ListQuest | undefined => {
    const lower = name.toLowerCase().trim();
    if (!lower) return undefined;
    const withLower = quests.map(q => ({ quest: q, lower: q.name.toLowerCase() }));
    const match = withLower.find(({ lower: l }) => l === lower) ?? withLower.find(({ lower: l }) => l.includes(lower) || lower.includes(l));
    return match?.quest;
};

export const getWeekDays = (result: RepeatParsingResult) => {
    if (!result.weekdays) return [];
    const weekDaysList = [...result.weekdays];
    if (weekDaysList.length === 0) return [];

    return weekDaysList.map(wd => capitalizeFirstLetter(wd) as Weekday);
};

export const getRepeatingType = (result: RepeatParsingResult) => {
    switch (result.repeats!) {
        case 'daily':
            return RepeatingType.Day;
        case 'weekly':
            return RepeatingType.Week;
        case 'monthly':
            return RepeatingType.Month;
        case 'yearly':
            return RepeatingType.Year;
    }
};

export const getRepeatFromStr = (query: string): [Repeat, RepeatParsingResult] | undefined => {
    const result = parse(query ?? '');
    if (!result || !result.repeats) return undefined;

    return [
        {
            query,
            repeatingType: getRepeatingType(result),
            monthDays: result.monthDay ? [result.monthDay] : [],
            weekDays: getWeekDays(result),
            interval: result.interval,
            startHour: result.startTime?.hours,
            startMinute: result.startTime?.minutes,
        },
        result,
    ];
};

export const dayTimeOptions = ['MorningRitual', 'Overdue', 'All day', 'Morning', 'Afternoon', 'Evening', 'EveningRitual'];
export const dayTimeIcons: Record<string, string> = {
    Overdue: 'timer-sand-complete',
    'All day': 'infinity',
    Morning: 'white-balance-sunny',
    Afternoon: 'briefcase',
    Evening: 'weather-sunset',
};
export const dayTimeIconColors: Record<string, string> = {
    Overdue: 'RED_500',
    'All day': 'BLUE_500',
    Morning: 'ORANGE_500',
    Afternoon: 'BROWN_700',
    Evening: 'DEEPPRUPLE_600',
};

export const dayTimeToTime: Record<string, string> = {
    Overdue: 'finish these first',
    'All day': '',
    Morning: 'until noon',
    Afternoon: 'until 5pm',
    Evening: 'after 5pm',
};

const repeatUnitKey = (
    repeatingType: RepeatingType,
): 'quest.repeatSummary.day' | 'quest.repeatSummary.week' | 'quest.repeatSummary.month' | 'quest.repeatSummary.year' => {
    switch (repeatingType) {
        case RepeatingType.Day:
            return 'quest.repeatSummary.day';
        case RepeatingType.Week:
            return 'quest.repeatSummary.week';
        case RepeatingType.Month:
            return 'quest.repeatSummary.month';
        case RepeatingType.Year:
            return 'quest.repeatSummary.year';
    }
};

type RepeatWeekdayKey =
    | 'quest.repeatWeekdays.Monday'
    | 'quest.repeatWeekdays.Tuesday'
    | 'quest.repeatWeekdays.Wednesday'
    | 'quest.repeatWeekdays.Thursday'
    | 'quest.repeatWeekdays.Friday'
    | 'quest.repeatWeekdays.Saturday'
    | 'quest.repeatWeekdays.Sunday';

const repeatWeekdayKey = (weekday: string): RepeatWeekdayKey =>
    `quest.repeatWeekdays.${capitalizeFirstLetter(weekday.toLocaleLowerCase())}` as RepeatWeekdayKey;

export const getRepeatText = (repeat: Repeat) => {
    const interval = repeat.interval ?? 1;
    const timeSection =
        repeat.startHour == null
            ? ''
            : i18next.t('quest.repeatAt', {
                  time: `${repeat.startHour}:${String(repeat.startMinute ?? 0).padStart(2, '0')}`,
              });
    const weekDays = repeat.weekDays ? [...repeat.weekDays] : [];
    const weekSection =
        weekDays.length > 0
            ? i18next.t('quest.repeatOn', {
                  days: weekDays
                      .map(repeatWeekdayKey)
                      .map(key => i18next.t(key))
                      .join(', '),
              })
            : '';
    const monthDays = repeat.monthDays ? [...repeat.monthDays] : [];
    const monthDaySection = monthDays.length > 0 ? i18next.t('quest.repeatOnDay', { days: monthDays.join(', ') }) : '';

    return `${i18next.t(repeatUnitKey(repeat.repeatingType), { count: interval })}${weekSection}${monthDaySection}${timeSection}`;
};

export const getNextDueDate = (quest: ListQuest) => {
    const isOverdue = isInPast(dayjs(quest.dueDate));
    return nextOccurrence(parse(quest?.repeat?.query ?? '')!, isOverdue ? dayjs().add(-1, 'day') : dayjs(quest.dueDate));
};

export const getVoicePostponeDate = (quest: ListQuest): dayjs.Dayjs => {
    if (quest.repeat?.query) {
        const parsed = parse(quest.repeat.query);
        if (parsed) {
            const today = dayjsStartOfDay(dayjs());
            const dueDayjs = quest.dueDate ? dayjs(quest.dueDate) : today;
            const isOverdue = isInPast(dueDayjs);
            const startDate = isOverdue ? today.add(-1, 'day') : dayjsStartOfDay(dueDayjs);
            return nextOccurrence(parsed, startDate);
        }
    }
    const dueDayjs = quest.dueDate ? dayjs(quest.dueDate) : dayjs();
    const dueHour = dueDayjs.hour();
    const dueMinute = dueDayjs.minute();
    const isOverdue = quest.dueDate && isInPast(dueDayjs);
    const base = dayjsStartOfDay(dayjs());
    return (isOverdue ? base : base.add(1, 'day')).add(dueHour, 'hour').add(dueMinute, 'minute');
};

export const getVoicePostponeToTomorrowDate = (quest: ListQuest): dayjs.Dayjs => {
    const dueDayjs = quest.dueDate ? dayjs(quest.dueDate) : dayjs();
    const dueHour = quest.repeat?.startHour ?? dueDayjs.hour();
    const dueMinute = quest.repeat?.startMinute ?? dueDayjs.minute();
    return dayjsStartOfDay(dayjs()).add(1, 'day').add(dueHour, 'hour').add(dueMinute, 'minute');
};

/**
 * Get the next due date for ritual quests with special overdue handling:
 * - Morning Ritual (overdue): Next is tomorrow morning
 * - Evening Ritual (overdue):
 *   - If before noon: Next is today evening
 *   - If after noon: Next is tomorrow evening
 */
export const getNextRitualDueDate = (quest: ListQuest, ritualType: 'Morning' | 'Evening') => {
    const isOverdue = isInPast(dayjs(quest.dueDate));

    if (!isOverdue) {
        return nextOccurrence(parse(quest?.repeat?.query ?? '')!, dayjs(quest.dueDate));
    }

    const now = dayjs();
    const startOfDay = now.startOf('day');
    const hoursFromStartOfDay = now.diff(startOfDay, 'hour', true);

    if (ritualType === 'Morning') {
        return nextOccurrence(parse(quest?.repeat?.query ?? '')!, now);
    } else {
        if (hoursFromStartOfDay < 12) {
            return now.startOf('day').hour(17).minute(0).second(0);
        } else {
            return nextOccurrence(parse(quest?.repeat?.query ?? '')!, now);
        }
    }
};

export const mapTimeOfDayToGraphQLTimeOfDay = (timeOfDay: TimeOfDay | null): GraphQLTimeOfDay | null => {
    switch (timeOfDay) {
        case 'MorningRitual':
            return GraphQLTimeOfDay.MorningRitual;
        case 'Overdue':
            return GraphQLTimeOfDay.Overdue;
        case 'All day':
            return GraphQLTimeOfDay.AllDay;
        case 'Morning':
            return GraphQLTimeOfDay.Morning;
        case 'Afternoon':
            return GraphQLTimeOfDay.Afternoon;
        case 'Evening':
            return GraphQLTimeOfDay.Evening;
        case 'EveningRitual':
            return GraphQLTimeOfDay.EveningRitual;
        default:
            return null;
    }
};

export const mapGraphQLTimeOfDayToTimeOfDay = (timeOfDay?: GraphQLTimeOfDay | null): TimeOfDay | null => {
    switch (timeOfDay) {
        case GraphQLTimeOfDay.MorningRitual:
            return 'MorningRitual';
        case GraphQLTimeOfDay.Overdue:
            return 'Overdue';
        case GraphQLTimeOfDay.AllDay:
            return 'All day';
        case GraphQLTimeOfDay.Morning:
            return 'Morning';
        case GraphQLTimeOfDay.Afternoon:
            return 'Afternoon';
        case GraphQLTimeOfDay.Evening:
            return 'Evening';
        case GraphQLTimeOfDay.EveningRitual:
            return 'EveningRitual';
        default:
            return null;
    }
};

export type CategorySummary = {
    categoryId: string;
    categoryTemplateId?: string | null;
    categoryName: string;
    categoryIcon: string;
    categoryColor: string;
    categoryCustomIcon?: string | null;
    totalXP: number;
    totalTimeSpent: number;
    questCount: number;
};

export const getCategorySummaries = (
    completedQuests: Array<{
        categoryId: string;
        xpEarned: number;
        timeSpent: number;
    }>,
    categoryMap: Record<string, { name: string; icon: string; iconColor: string; customIcon?: string | null; templateId?: string | null }>,
): CategorySummary[] => {
    const summaryMap = completedQuests.reduce((map, quest) => {
        const category = categoryMap[quest.categoryId];
        if (!category) return map;

        const existing = map.get(quest.categoryId);
        if (existing) {
            existing.totalXP += quest.xpEarned;
            existing.totalTimeSpent += quest.timeSpent;
            existing.questCount += 1;
        } else {
            map.set(quest.categoryId, {
                categoryId: quest.categoryId,
                categoryTemplateId: category.templateId,
                categoryName: category.name,
                categoryIcon: category.icon,
                categoryColor: category.iconColor,
                categoryCustomIcon: category.customIcon,
                totalXP: quest.xpEarned,
                totalTimeSpent: quest.timeSpent,
                questCount: 1,
            });
        }
        return map;
    }, new Map<string, CategorySummary>());

    return Array.from(summaryMap.values()).sort((a, b) => b.totalXP - a.totalXP);
};
